import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Loader2, Save, Plus, Pencil, Trash2, Settings2, Building2, Bell, Shield, DollarSign, Mail, Phone, MapPin, Calendar, AlertTriangle } from "lucide-react";
import { ProhibitedCategoryDialog } from "@/components/settings/ProhibitedCategoryDialog";
import { DashboardPreferences } from "@/components/dashboard/DashboardPreferences";
import { WhiteLabelSettings } from "@/components/settings/WhiteLabelSettings";
import { FeatureModules } from "@/components/settings/FeatureModules";
import IntegrationSettings from "@/pages/settings/IntegrationSettings";
import { Sparkles } from "lucide-react";

const Settings = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<any>(null);

  const { data: settings, refetch } = useQuery({
    queryKey: ["institution-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("institution_settings" as any)
        .select("*")
        .limit(1)
        .maybeSingle();

      if (error && error.code !== "PGRST116") throw error;
      return data as any;
    },
  });

  const { data: prohibitedCategories } = useQuery({
    queryKey: ["prohibited-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("prohibited_categories" as any)
        .select("*")
        .order("name");

      if (error) throw error;
      return data as any[];
    },
  });

  const handleSaveSettings = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const formData = new FormData(e.currentTarget);
      const updates = {
        auto_approve_threshold: parseFloat(formData.get("auto_approve_threshold") as string),
        mandatory_review_threshold: parseFloat(formData.get("mandatory_review_threshold") as string),
        high_value_threshold: parseFloat(formData.get("high_value_threshold") as string),
        disclosure_deadline_days: parseInt(formData.get("disclosure_deadline_days") as string),
        school_ip_contact_email: formData.get("school_ip_contact_email") as string,
        compliance_officer_emails: (formData.get("compliance_officer_emails") as string).split(",").map(e => e.trim()),
        athletic_director_email: formData.get("athletic_director_email") as string,
        auto_analyze_enabled: settings?.auto_analyze_enabled,
      };

      const { error } = await supabase
        .from("institution_settings" as any)
        .update(updates as any)
        .eq("id", settings?.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Settings saved successfully",
      });

      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProfile = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const formData = new FormData(e.currentTarget);
      const updates = {
        institution_name: formData.get("institution_name") as string,
        website: formData.get("website") as string,
        main_phone: formData.get("main_phone") as string,
        address_line1: formData.get("address_line1") as string,
        address_line2: formData.get("address_line2") as string,
        city: formData.get("city") as string,
        state: formData.get("state") as string,
        zip_code: formData.get("zip_code") as string,
        timezone: formData.get("timezone") as string,
        academic_year: formData.get("academic_year") as string,
      };

      const { error } = await supabase
        .from("institution_settings" as any)
        .update(updates as any)
        .eq("id", settings?.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Settings saved successfully",
      });

      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    setIsSaving(true);

    try {
      const updates = {
        notification_frequency: settings?.notification_frequency,
        notify_deal_submitted: settings?.notify_deal_submitted,
        notify_deal_approved: settings?.notify_deal_approved,
        notify_deal_rejected: settings?.notify_deal_rejected,
        notify_high_value_deal: settings?.notify_high_value_deal,
        notify_conflict_detected: settings?.notify_conflict_detected,
        notify_deadline_approaching: settings?.notify_deadline_approaching,
        notify_athlete_status_change: settings?.notify_athlete_status_change,
      };

      const { error } = await supabase
        .from("institution_settings" as any)
        .update(updates as any)
        .eq("id", settings?.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Notification settings saved successfully",
      });

      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      const { error } = await supabase
        .from("prohibited_categories" as any)
        .delete()
        .eq("id", categoryToDelete.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Category deleted successfully",
      });

      queryClient.invalidateQueries({ queryKey: ["prohibited-categories"] });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    }
  };

  const openEditDialog = (category: any) => {
    setSelectedCategory(category);
    setCategoryDialogOpen(true);
  };

  const openCreateDialog = () => {
    setSelectedCategory(null);
    setCategoryDialogOpen(true);
  };

  const openDeleteDialog = (category: any) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  if (!settings) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Settings2 className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Institution Settings</h1>
            <p className="text-muted-foreground mt-1">
              Configure NIL compliance policies and thresholds
            </p>
          </div>
        </div>

        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="grid w-full grid-cols-8 lg:w-auto lg:inline-flex">
            <TabsTrigger value="general" className="gap-2">
              <DollarSign className="h-4 w-4" />
              <span className="hidden sm:inline">General</span>
            </TabsTrigger>
            <TabsTrigger value="profile" className="gap-2">
              <Building2 className="h-4 w-4" />
              <span className="hidden sm:inline">Profile</span>
            </TabsTrigger>
            <TabsTrigger value="features" className="gap-2">
              <Sparkles className="h-4 w-4" />
              <span className="hidden sm:inline">Features</span>
            </TabsTrigger>
            <TabsTrigger value="integrations" className="gap-2">
              <Settings2 className="h-4 w-4" />
              <span className="hidden sm:inline">Integrations</span>
            </TabsTrigger>
            <TabsTrigger value="dashboard" className="gap-2">
              <Settings2 className="h-4 w-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="branding" className="gap-2">
              <Settings2 className="h-4 w-4" />
              <span className="hidden sm:inline">Branding</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2">
              <Bell className="h-4 w-4" />
              <span className="hidden sm:inline">Notifications</span>
            </TabsTrigger>
            <TabsTrigger value="categories" className="gap-2">
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Categories</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-6">
            <Card className="border-2 hover:border-primary/20 transition-colors">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  <CardTitle>Compliance Thresholds</CardTitle>
                </div>
                <CardDescription>
                  Configure automatic approval thresholds and review requirements
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveSettings} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="auto_approve_threshold">
                        Auto-Approve Threshold ($)
                      </Label>
                      <Input
                        id="auto_approve_threshold"
                        name="auto_approve_threshold"
                        type="number"
                        step="0.01"
                        defaultValue={settings.auto_approve_threshold}
                      />
                      <p className="text-xs text-muted-foreground">
                        Deals below this amount can be auto-approved if no risk flags
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="mandatory_review_threshold">
                        Mandatory Review Threshold ($)
                      </Label>
                      <Input
                        id="mandatory_review_threshold"
                        name="mandatory_review_threshold"
                        type="number"
                        step="0.01"
                        defaultValue={settings.mandatory_review_threshold}
                      />
                      <p className="text-xs text-muted-foreground">
                        Deals above this amount require athletic director approval
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="high_value_threshold">
                        High-Value Flag Threshold ($)
                      </Label>
                      <Input
                        id="high_value_threshold"
                        name="high_value_threshold"
                        type="number"
                        step="0.01"
                        defaultValue={settings.high_value_threshold}
                      />
                      <p className="text-xs text-muted-foreground">
                        Deals above this amount trigger automatic AI analysis
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="disclosure_deadline_days">
                        Disclosure Deadline (Days)
                      </Label>
                      <Input
                        id="disclosure_deadline_days"
                        name="disclosure_deadline_days"
                        type="number"
                        defaultValue={settings.disclosure_deadline_days}
                      />
                      <p className="text-xs text-muted-foreground">
                        Number of days athletes have to disclose deals
                      </p>
                    </div>
                  </div>

                  <Separator className="my-6" />

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold">AI Analysis Settings</h3>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <Label htmlFor="auto_analyze_enabled" className="text-base font-medium">
                          Auto-Analyze High-Value Deals
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Automatically run AI compliance analysis on deals above the high-value threshold
                        </p>
                      </div>
                      <Switch
                        id="auto_analyze_enabled"
                        checked={settings.auto_analyze_enabled !== false}
                        onCheckedChange={(checked) => {
                          queryClient.setQueryData(["institution-settings"], {
                            ...settings,
                            auto_analyze_enabled: checked,
                          });
                        }}
                      />
                    </div>
                  </div>

                  <Separator className="my-6" />

                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold">Contact Information</h3>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="compliance_officer_emails">
                        Compliance Officer Emails (comma-separated)
                      </Label>
                      <Input
                        id="compliance_officer_emails"
                        name="compliance_officer_emails"
                        type="text"
                        defaultValue={settings.compliance_officer_emails?.join(", ") || ""}
                        placeholder="officer1@university.edu, officer2@university.edu"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="athletic_director_email">
                        Athletic Director Email
                      </Label>
                      <Input
                        id="athletic_director_email"
                        name="athletic_director_email"
                        type="email"
                        defaultValue={settings.athletic_director_email || ""}
                        placeholder="ad@university.edu"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="school_ip_contact_email">
                        School IP Approval Contact
                      </Label>
                      <Input
                        id="school_ip_contact_email"
                        name="school_ip_contact_email"
                        type="email"
                        defaultValue={settings.school_ip_contact_email || ""}
                        placeholder="marketing@university.edu"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" disabled={isSaving}>
                      {isSaving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          Save Settings
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="features" className="space-y-6">
            <FeatureModules settings={settings} />
          </TabsContent>

          <TabsContent value="dashboard" className="space-y-6">
            <DashboardPreferences />
          </TabsContent>

          <TabsContent value="profile" className="space-y-6">
            <Card className="border-2 hover:border-primary/20 transition-colors">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  <CardTitle>Institution Profile</CardTitle>
                </div>
                <CardDescription>
                  Manage your institution's basic information and settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveProfile} className="space-y-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="institution_name">Institution Name</Label>
                      <Input
                        id="institution_name"
                        name="institution_name"
                        type="text"
                        defaultValue={settings.institution_name || ""}
                        placeholder="University Name"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="website">Website</Label>
                        <Input
                          id="website"
                          name="website"
                          type="url"
                          defaultValue={settings.website || ""}
                          placeholder="https://university.edu"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="main_phone">Main Phone</Label>
                        <Input
                          id="main_phone"
                          name="main_phone"
                          type="tel"
                          defaultValue={settings.main_phone || ""}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold">Address</h3>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="address_line1">Address Line 1</Label>
                        <Input
                          id="address_line1"
                          name="address_line1"
                          type="text"
                          defaultValue={settings.address_line1 || ""}
                          placeholder="123 Main Street"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="address_line2">Address Line 2</Label>
                        <Input
                          id="address_line2"
                          name="address_line2"
                          type="text"
                          defaultValue={settings.address_line2 || ""}
                          placeholder="Suite 100"
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="city">City</Label>
                          <Input
                            id="city"
                            name="city"
                            type="text"
                            defaultValue={settings.city || ""}
                            placeholder="City"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="state">State</Label>
                          <Input
                            id="state"
                            name="state"
                            type="text"
                            defaultValue={settings.state || ""}
                            placeholder="ST"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="zip_code">ZIP Code</Label>
                          <Input
                            id="zip_code"
                            name="zip_code"
                            type="text"
                            defaultValue={settings.zip_code || ""}
                            placeholder="12345"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="academic_year">Academic Year</Label>
                        <Input
                          id="academic_year"
                          name="academic_year"
                          type="text"
                          defaultValue={settings.academic_year || ""}
                          placeholder="2024-2025"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="timezone">Timezone</Label>
                        <Input
                          id="timezone"
                          name="timezone"
                          type="text"
                          defaultValue={settings.timezone || "America/New_York"}
                          placeholder="America/New_York"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" disabled={isSaving}>
                      {isSaving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          Save Profile
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="branding" className="space-y-6">
            <WhiteLabelSettings />
          </TabsContent>

          <TabsContent value="notifications" className="space-y-6">
            <Card className="border-2 hover:border-primary/20 transition-colors">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-primary" />
                  <CardTitle>Notification Settings</CardTitle>
                </div>
                <CardDescription>
                  Configure how and when compliance officers receive notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="notification_frequency">Notification Frequency</Label>
                    <Select
                      value={settings?.notification_frequency || "instant"}
                      onValueChange={(value) => {
                        if (settings) {
                          refetch().then(() => {
                            // Update local state after refetch
                          });
                        }
                      }}
                    >
                      <SelectTrigger id="notification_frequency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="instant">Instant (Real-time)</SelectItem>
                        <SelectItem value="daily">Daily Digest</SelectItem>
                        <SelectItem value="weekly">Weekly Summary</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-primary" />
                      <h4 className="font-medium">Email Notifications</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Choose which events trigger email notifications
                    </p>

                    <div className="space-y-4 pt-2">
                      <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="space-y-0.5 flex-1">
                          <div className="flex items-center gap-2">
                            <Bell className="h-4 w-4 text-muted-foreground" />
                            <Label htmlFor="notify_deal_submitted" className="cursor-pointer">Deal Submitted</Label>
                          </div>
                          <p className="text-sm text-muted-foreground pl-6">
                            When an athlete submits a new deal for review
                          </p>
                        </div>
                        <Switch
                          id="notify_deal_submitted"
                          checked={settings?.notify_deal_submitted ?? true}
                          onCheckedChange={(checked) => {
                            if (settings) {
                              settings.notify_deal_submitted = checked;
                            }
                          }}
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="space-y-0.5 flex-1">
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-muted-foreground" />
                            <Label htmlFor="notify_deal_approved" className="cursor-pointer">Deal Approved</Label>
                          </div>
                          <p className="text-sm text-muted-foreground pl-6">
                            When a deal is approved by compliance
                          </p>
                        </div>
                        <Switch
                          id="notify_deal_approved"
                          checked={settings?.notify_deal_approved ?? true}
                          onCheckedChange={(checked) => {
                            if (settings) {
                              settings.notify_deal_approved = checked;
                            }
                          }}
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="space-y-0.5 flex-1">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                            <Label htmlFor="notify_deal_rejected" className="cursor-pointer">Deal Rejected</Label>
                          </div>
                          <p className="text-sm text-muted-foreground pl-6">
                            When a deal is rejected by compliance
                          </p>
                        </div>
                        <Switch
                          id="notify_deal_rejected"
                          checked={settings?.notify_deal_rejected ?? true}
                          onCheckedChange={(checked) => {
                            if (settings) {
                              settings.notify_deal_rejected = checked;
                            }
                          }}
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="space-y-0.5 flex-1">
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            <Label htmlFor="notify_high_value_deal" className="cursor-pointer">High-Value Deal</Label>
                          </div>
                          <p className="text-sm text-muted-foreground pl-6">
                            When a deal exceeds the high-value threshold
                          </p>
                        </div>
                        <Switch
                          id="notify_high_value_deal"
                          checked={settings?.notify_high_value_deal ?? true}
                          onCheckedChange={(checked) => {
                            if (settings) {
                              settings.notify_high_value_deal = checked;
                            }
                          }}
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="space-y-0.5 flex-1">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                            <Label htmlFor="notify_conflict_detected" className="cursor-pointer">Conflict Detected</Label>
                          </div>
                          <p className="text-sm text-muted-foreground pl-6">
                            When a deal conflicts with institutional sponsors
                          </p>
                        </div>
                        <Switch
                          id="notify_conflict_detected"
                          checked={settings?.notify_conflict_detected ?? true}
                          onCheckedChange={(checked) => {
                            if (settings) {
                              settings.notify_conflict_detected = checked;
                            }
                          }}
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="space-y-0.5 flex-1">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <Label htmlFor="notify_deadline_approaching" className="cursor-pointer">Deadline Approaching</Label>
                          </div>
                          <p className="text-sm text-muted-foreground pl-6">
                            When a disclosure deadline is within 2 days
                          </p>
                        </div>
                        <Switch
                          id="notify_deadline_approaching"
                          checked={settings?.notify_deadline_approaching ?? true}
                          onCheckedChange={(checked) => {
                            if (settings) {
                              settings.notify_deadline_approaching = checked;
                            }
                          }}
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="space-y-0.5 flex-1">
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-muted-foreground" />
                            <Label htmlFor="notify_athlete_status_change" className="cursor-pointer">Athlete Status Change</Label>
                          </div>
                          <p className="text-sm text-muted-foreground pl-6">
                            When an athlete's eligibility status changes
                          </p>
                        </div>
                        <Switch
                          id="notify_athlete_status_change"
                          checked={settings?.notify_athlete_status_change ?? false}
                          onCheckedChange={(checked) => {
                            if (settings) {
                              settings.notify_athlete_status_change = checked;
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSaveNotifications} disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Notification Settings
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="categories" className="space-y-6">
            <Card className="border-2 hover:border-primary/20 transition-colors">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" />
                    <CardTitle>Prohibited Categories</CardTitle>
                  </div>
                  <CardDescription>
                    Categories that are automatically blocked or flagged for review
                  </CardDescription>
                </div>
                <Button onClick={openCreateDialog} size="sm" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Add Category
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {prohibitedCategories?.length === 0 ? (
                    <div className="text-center py-12 px-4">
                      <Shield className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                      <p className="text-muted-foreground font-medium">No prohibited categories yet</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Click "Add Category" to create one.
                      </p>
                    </div>
                  ) : (
                    prohibitedCategories?.map((category) => (
                      <div
                        key={category.id}
                        className="flex items-start justify-between p-4 border-2 rounded-lg hover:border-primary/30 hover:bg-accent/50 transition-all group"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Shield className="h-4 w-4 text-primary" />
                            <h4 className="font-semibold">{category.name}</h4>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 mb-3">
                            {category.description || "No description"}
                          </p>
                          {category.keywords && category.keywords.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {category.keywords.map((keyword, idx) => (
                                <span key={idx} className="inline-block px-2 py-0.5 text-xs rounded-md bg-secondary text-secondary-foreground">
                                  {keyword}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <span
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full ${
                              category.severity === "blocking"
                                ? "bg-destructive/10 text-destructive border border-destructive/20"
                                : "bg-yellow-500/10 text-yellow-700 dark:text-yellow-500 border border-yellow-500/20"
                            }`}
                          >
                            <AlertTriangle className="h-3 w-3" />
                            {category.severity}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(category)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteDialog(category)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <ProhibitedCategoryDialog
        open={categoryDialogOpen}
        onOpenChange={setCategoryDialogOpen}
        category={selectedCategory}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{categoryToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCategory} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default Settings;